import { Alert, Button, Grid } from '@mui/material';
import { FormEvent, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { base64ToBlob, exportFile } from '../../../../utils';
import { UserContext } from '../../../../components/shared/useUser';
import useLogError from '../../../../hooks/useLogError';
import { ForecastOrdersReportRequest, client } from './../../../../app/services/api/orderManagementClient';
import DatePickerLocalized from '../../../../components/shared/DatePickerLocalized';
import { MultiStorePicker, StoreSelection } from '../../../../components/shared/MultiStorePicker';

type IProps = {
  onClose?: () => void;
}

const ForecastOrdersReportForm = (props: IProps) => {
  const { user, selectedStore } = useContext(UserContext);
  const [storeSelection, setStoreSelection] = useState<StoreSelection>({ mode: 'all' });
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const { logError } = useLogError();
  const { t } = useTranslation('common');
  const { t: datepickerLabels } = useTranslation('orderHistory');

  const currentFranchiseStores = user?.stores?.filter(x => x.franchiseId === selectedStore?.franchiseId) ?? [];

  const getMaxDate = () => {
    const today = new Date();
    today.setDate(today.getDate() - 1);
    const yesterday = today.toISOString().split('T')[0];
    return yesterday;
  };

  const getMinDate = () => {
    const minDate = new Date('1900-01-01').toISOString().split('T')[0];
    return minDate;
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setIsGenerating(true);
    setErrorMessage('');
    setSuccessMessage('');

    const selectedAllStores = storeSelection.mode === 'all';
    const storeNumbers = selectedAllStores 
      ? currentFranchiseStores.map(x => x.storeNumber as string) 
      : storeSelection.mode === 'specific' ? storeSelection.storeNumbers : [];

    const request: ForecastOrdersReportRequest = {
      storeNumbers: storeNumbers ?? [],
      fromDate: fromDate,
      toDate: toDate,
    };

    client.generateForecastOrdersReport(selectedStore?.storeNumber ?? '', request).then((result) => {
      if (!result.success) {
        setErrorMessage(result.message);
        return;
      }
      exportFile(base64ToBlob(result.data.fileData), 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', result.data.fileName);
      props.onClose?.();
    }).catch((error) => {
      if (typeof(error) === 'string') {
        setErrorMessage(error);
      }
      logError(error);
    }).finally(() => {
      setIsGenerating(false);
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container
        spacing={10}
        sx={{ pt: '10px' }}>
        <Grid item
          sm={12}
          xs={12}>
          <MultiStorePicker
            stores={currentFranchiseStores}
            value={storeSelection}
            onChange={setStoreSelection}
            allStoresLabel={t('reporting.all-stores')}
          />
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <DatePickerLocalized
            label={t('reporting.start-date')}
            value={fromDate}
            onChange={(newValue) => setFromDate(newValue ?? '')}
            useCalendar={false}
            shouldDisableDate={(date: string) =>
              date < getMinDate() || date > (toDate ? toDate : getMaxDate())
            }
            translations={{
              clearButtonLabel: datepickerLabels('calendar.clearButtonLabel'),
              todayButtonLabel: datepickerLabels('calendar.todayButtonLabel'),
              cancelButtonLabel: datepickerLabels('calendar.cancelButtonLabel'),
              okButtonLabel: datepickerLabels('calendar.okButtonLabel'),
              datePickerToolbarTitle: t('reporting.start-date'),
            }}
          />
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <DatePickerLocalized
            label={t('reporting.end-date')}
            value={toDate}
            onChange={(newValue) => setToDate(newValue ?? '')}
            useCalendar={false}
            shouldDisableDate={(date: string) =>
              date < (fromDate ? fromDate : getMinDate()) || date > getMaxDate()
            }
            translations={{
              clearButtonLabel: datepickerLabels('calendar.clearButtonLabel'),
              todayButtonLabel: datepickerLabels('calendar.todayButtonLabel'),
              cancelButtonLabel: datepickerLabels('calendar.cancelButtonLabel'),
              okButtonLabel: datepickerLabels('calendar.okButtonLabel'),
              datePickerToolbarTitle: t('reporting.end-date'),
            }}
          />
        </Grid>
        {
          errorMessage && 
          <Grid item
            xs={12}>
            <Alert severity="error">{errorMessage}</Alert>
          </Grid>
        }
        {
          successMessage && 
          <Grid item
            xs={12}>
            <Alert severity="success">{successMessage}</Alert>
          </Grid>
        }
        <Grid item
          xs={12}
          sx={{ display: 'flex', justifyContent: 'right', columnGap: '10px' }}>
          <Button size='lg'
            variant='secondary' 
            onClick={props.onClose}>{t('cancel')}</Button>
          <Button size='lg'
            disabled={
              isGenerating ||
              !fromDate ||
              !toDate
            }
            type='submit'>{t('reporting.generate')}</Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default ForecastOrdersReportForm;